import { FormikProps } from 'formik';
import { get } from 'lodash';
import { useCallback, useState } from 'react';
import { Tabs, Divider, Space, Row, Col, Spin } from 'antd';
import { EditOutlined, UpOutlined } from '@ant-design/icons';
import NewFormCard, { FormCardContainer } from 'views/declarations/common/cards/NewFormCard';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import { H3 } from 'components/new-ui/base/typography/styles';
import CustomerSelect from 'components/new-ui/composed/selects/CustomerSelect';
import useCustomers from 'hooks/useCustomers';
import { Customer } from 'store/customers/customer';
import { RefDiv } from 'components/ui/composed/declarations/formInput/DeclarationInput.styles';
import Button from 'components/new-ui/base/button/Button';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import addPathPrefix from 'utils/addPathPrefix';
import DeclarationInput from 'components/ui/composed/declarations/formInput';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import MultipleItemsCard from 'views/declarations/common/MultipleItemsCard';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { ICS2Address, ICS2Party } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { deepObjHasTruthyNonObjValue } from 'core/utils/objects';
import useTemplatePath from 'components/ui/composed/template/useTemplatePath';
import { useRevealDeclarationField } from 'views/declarations/utils/revealDeclarationField';
import { isPostcodeOptional } from 'views/declarations/eu/ics2/validation/postcodeOptionalCountries';

type Party = {
    tabName: string;
    label: string;
    required: boolean;
    eoriRequired?: boolean;
    communicationRequired?: boolean;
    singleCommunication?: boolean;
    hasStatus?: boolean;
    path: string;
    referenceNumber?: string;
    partyReferences: {
        name: string;
        identificationNumber: string;
        streetAndNumber: string;
        postCode: string;
        city: string;
        country: string;
        typeOfPerson?: string;
    };
};

type Props = {
    parties: Party[];
    formik: FormikProps<any>;
    hidden?: boolean;
    viewOnly: boolean | undefined;
};

/**
 * The fields marked touched when a customer is picked or cleared. `typeOfPerson` is among them even
 * though a customer does not carry one: it is mandatory, so what is still missing has to show at the
 * moment the rest of the party is filled in rather than on the next blur.
 */
const PARTY_FIELDS = [
    'customerId',
    'identificationNumber',
    'name',
    'typeOfPerson',
    'address.street',
    'address.number',
    'address.poBox',
    'address.city',
    'address.postCode',
    'address.country',
];

const PartiesCard = ({ parties, formik, hidden, viewOnly }: Props) => {
    const [activeTabKey, setActiveTabKey] = useState<string>(parties[0]?.tabName);
    const [expanded, setExpanded] = useState(true);
    const [filling, setFilling] = useState(false);
    const { getCustomer } = useCustomers();

    /**
     * The card reads and writes whole parties itself, so its paths have to be rebased onto the
     * slot being edited when this renders inside the template builder. The field components
     * below do that for themselves — see the note on the hook before using it on their paths.
     */
    const scoped = useTemplatePath();

    /**
     * What the message requires of a party applies once the party is there, not before: an optional
     * one still has to name itself and give an address, but only from the moment anything is entered
     * into it. The validators express that by skipping an empty optional party altogether, so the
     * asterisks follow the same rule and mark exactly what the validators are about to reject.
     */
    const fieldsRequired = (party: Party) =>
        party.required || deepObjHasTruthyNonObjValue(get(formik.values, scoped(party.path)));

    /**
     * The customer itself is not part of the declaration - what customs needs is the party details.
     * So picking one copies them onto the party, overwriting whatever is there, and clearing the
     * selection takes them away again. `customerId` rides along for the select to show its choice,
     * but the backend drops it, so it only survives until the declaration is reloaded.
     */
    const applyCustomerToParty = useCallback(
        async (path: string, customerId?: string) => {
            // Touched as well as set: picking a customer is a user change, and the flows that save
            // before navigating away look for touched fields to decide there is anything to save.
            const touchPartyFields = () =>
                PARTY_FIELDS.forEach((field) =>
                    formik.setFieldTouched(scoped(addPathPrefix(path, field)), true, false)
                );

            /**
             * The party is written in one go, and validated with it: the form does not validate on
             * change, so field by field writes would leave the messages of the last run on screen
             * until the user blurred something. `setFieldValue` validates against the values it is
             * about to hold rather than against React state, so one write is also one consistent
             * snapshot - and one run of validators that are async and partly call out to the EORI
             * check service.
             *
             * What the card does not manage - `typeOfPerson`, `communication` - is carried over.
             */
            const writeParty = (party: ICS2Party | null) => formik.setFieldValue(scoped(path), party, true);

            const current: ICS2Party = get(formik.values, scoped(path)) ?? {};

            if (!customerId) {
                touchPartyFields();

                const remaining: ICS2Party = {
                    ...current,
                    customerId: undefined,
                    identificationNumber: undefined,
                    name: undefined,
                    address: undefined,
                };

                /**
                 * Clearing the customer takes away everything that identified the party - its name,
                 * EORI and address. An optional party left with only what the card does not manage,
                 * a type of person or a status, is no longer a party at all, and keeping it as an
                 * object is worse than dropping it: the validators walk into a party that is merely
                 * empty and report every field it requires once present as missing, on a party the
                 * message structure does not ask for and with no way to clear it from the form. It
                 * would reach the backend as an empty element too. A required party keeps what was
                 * typed - it is reported as incomplete either way, and has to be filled in anyway.
                 */
                const isOptional = !parties.find((party) => party.path === path)?.required;

                await writeParty(isOptional ? null : remaining);
                return;
            }

            const dropSelection = () =>
                formik.setFieldValue(scoped(addPathPrefix(path, 'customerId')), undefined, false);

            // Shown in the select straight away, without waiting on the customer to be fetched.
            formik.setFieldValue(scoped(addPathPrefix(path, 'customerId')), customerId, false);

            /**
             * The card is covered while the customer is fetched and written, which also keeps a second
             * pick from being overwritten by the first one's late response. A customer that does
             * not come back is taken off the select again, which would otherwise name a customer
             * whose details never reached the party.
             */
            setFilling(true);

            try {
                const customer: Customer | undefined = await getCustomer(customerId);
                if (!customer) {
                    dropSelection();
                    return;
                }

                touchPartyFields();

                await writeParty({
                    ...current,
                    customerId,
                    identificationNumber: customer.eori,
                    name: customer.name,
                    address: {
                        ...current.address,
                        street: customer.address?.addressLine1,
                        streetAdditionalLine: customer.address?.addressLine2 ?? undefined,
                        city: customer.address?.city,
                        postCode: customer.address?.postCode,
                        country: customer.address?.country,
                    },
                });
            } catch (error) {
                dropSelection();
                throw error;
            } finally {
                setFilling(false);
            }
        },
        [formik, getCustomer, parties, scoped]
    );

    /**
     * Fields commit on blur, even when nothing was typed, and the form validates the whole
     * declaration - houses and items included - on every write. So a value is only written when it
     * changed, and written once, with validation: the form does not validate on change, and a
     * select's clear button does not blur it, so the messages of the previous run would otherwise
     * stay on screen. Blurring only marks the field touched, without a validation of its own.
     */
    const validatedFieldProps = useCallback(
        (fieldPath: string, onChanged?: () => void) => {
            const props = getFormikProps(fieldPath, formik);
            const { name, value: current } = props.fieldProps;

            return {
                ...props,
                fieldProps: {
                    ...props.fieldProps,
                    onChange: (event: any) => {
                        const value = event?.target?.value;
                        if ((value ?? '') === (current ?? '')) return;

                        onChanged?.();
                        formik.setFieldValue(name, value, true);
                    },
                    onBlur: () => formik.setFieldTouched(name, true, false),
                },
                fieldHelper: {
                    ...props.fieldHelper,
                    setTouched: (touched = true) => formik.setFieldTouched(name, touched, false),
                },
            };
        },
        [formik]
    );

    /**
     * Once a party is edited by hand it no longer matches the customer it was filled from, so the
     * selection is dropped - keeping it would leave the select naming a customer whose details are
     * not the ones on the declaration.
     */
    const partyFieldProps = (path: string, field: string) =>
        validatedFieldProps(addPathPrefix(path, field), () =>
            formik.setFieldValue(scoped(addPathPrefix(path, 'customerId')), undefined, false)
        );

    /**
     * Only the active tab is rendered, so a field of another party is not in the DOM for navigation
     * to find. A request for anything under a party opens its tab, with the details expanded.
     */
    useRevealDeclarationField((fieldPath) => {
        const isWithin = (path: string) => fieldPath === path || fieldPath.startsWith(`${path}.`);
        const party = parties.find(({ path }) => isWithin(path) || isWithin(scoped(path)));

        if (!party) return false;

        setActiveTabKey(party.tabName);
        setExpanded(true);

        return true;
    });

    /**
     * Only the active tab is built. A tab that was visited stays mounted, so building the others as
     * well would re-render all of them on every keystroke for fields that are on screen in one.
     */
    const tabItems = parties.map((party) => {
        if (party.tabName !== activeTabKey) return { key: party.tabName, label: party.tabName, children: null };

        const required = fieldsRequired(party);

        /**
         * R3002 and C3034, marked the way the validators apply them: a street and a number
         * unless a P.O. Box stands in for them, and a postcode unless the country has none.
         * Nothing is asked of the postcode before there is a country to decide on.
         */
        const address: ICS2Address = get(formik.values, scoped(party.path))?.address ?? {};
        const hasPoBox = Boolean(address.poBox?.trim());
        const streetAndNumberRequired = required && !hasPoBox;
        const country = address.country;
        const postcodeRequired = required && Boolean(country) && !isPostcodeOptional(country!);

        return {
            key: party.tabName,
            label: party.tabName,
            children: (
                <>
                    <Row>
                        <Col>
                            <Space size={8}>
                                {party.referenceNumber ? <RefDiv>{party.referenceNumber}</RefDiv> : null}
                                {party.required ? <LabelRequiredSymbol /> : null}
                                {party.label}:
                            </Space>
                        </Col>
                        <Col flex={1}>
                            <CustomerSelect
                                disabled={viewOnly}
                                customerId={get(formik.values, scoped(party.path))?.customerId}
                                onChange={(val) => val && applyCustomerToParty(party.path, val)}
                                onClear={() => applyCustomerToParty(party.path)}
                            />
                        </Col>
                    </Row>

                    <Divider orientation="left">
                        <strong>
                            <Space size={8}>
                                or
                                {expanded && (
                                    <Button
                                        style={{ color: 'var(--brand-6)' }}
                                        onClick={() => setExpanded(false)}
                                        icon={<UpOutlined />}
                                        type="link"
                                    ></Button>
                                )}
                            </Space>
                        </strong>
                    </Divider>

                    {!expanded && (
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            style={{ color: 'var(--brand-6)', fontSize: 14, marginLeft: 16 }}
                            onClick={() => setExpanded(true)}
                        >
                            Manually enter details
                        </Button>
                    )}

                    {expanded && (
                        <FormCardContainer>
                            <DeclarationInput
                                required={!!party.eoriRequired && required}
                                refNumber={party.partyReferences.identificationNumber}
                                label="Identification number"
                                {...partyFieldProps(party.path, 'identificationNumber')}
                                condensed
                            />
                            <DeclarationInput
                                required={required}
                                refNumber={party.partyReferences.name}
                                label="Name"
                                {...partyFieldProps(party.path, 'name')}
                                condensed
                            />
                            {party.partyReferences.typeOfPerson && (
                                <DeclarationSelect
                                    required={required}
                                    hideCodelistMenu
                                    label="Type of Person"
                                    refNumber={party.partyReferences.typeOfPerson}
                                    codeValidation={[]}
                                    condensed
                                    {...partyFieldProps(party.path, 'typeOfPerson')}
                                    codelistProps={{
                                        codelistName: ICS2CodelistsMap.CL729,
                                        declarationType: DeclarationTypes.ICS2,
                                    }}
                                />
                            )}
                            {party.hasStatus && (
                                <DeclarationSelect
                                    required={required}
                                    hideCodelistMenu
                                    label="Status"
                                    codeValidation={[]}
                                    condensed
                                    {...partyFieldProps(party.path, 'status')}
                                    codelistProps={{
                                        codelistName: ICS2CodelistsMap.CL094,
                                        declarationType: DeclarationTypes.ICS2,
                                    }}
                                />
                            )}
                            <DeclarationInput
                                required={streetAndNumberRequired}
                                refNumber={party.partyReferences.streetAndNumber}
                                label="Street"
                                {...partyFieldProps(party.path, 'address.street')}
                                condensed
                            />
                            <DeclarationInput
                                label="Street Additional Line"
                                {...partyFieldProps(party.path, 'address.streetAdditionalLine')}
                                condensed
                            />
                            <DeclarationInput
                                required={streetAndNumberRequired}
                                label="Number"
                                {...partyFieldProps(party.path, 'address.number')}
                                condensed
                            />
                            <DeclarationInput
                                label="P.O. Box"
                                {...partyFieldProps(party.path, 'address.poBox')}
                                condensed
                            />
                            <DeclarationInput
                                required={required}
                                refNumber={party.partyReferences.city}
                                label="City"
                                {...partyFieldProps(party.path, 'address.city')}
                                condensed
                            />
                            <DeclarationInput
                                label="Sub-division"
                                {...partyFieldProps(party.path, 'address.subDivision')}
                                condensed
                            />
                            <DeclarationInput
                                required={postcodeRequired}
                                refNumber={party.partyReferences.postCode}
                                label="Post Code"
                                {...partyFieldProps(party.path, 'address.postCode')}
                                condensed
                            />
                            <DeclarationSelect
                                required={required}
                                label="Country"
                                refNumber={party.partyReferences.country}
                                condensed
                                codeValidation={[]}
                                {...partyFieldProps(party.path, 'address.country')}
                                codelistProps={{
                                    codelistName: ICS2CodelistsMap.CL718,
                                    declarationType: DeclarationTypes.ICS2,
                                }}
                            />
                        </FormCardContainer>
                    )}

                    {party.singleCommunication ? (
                        <div style={{ marginTop: 20 }}>
                            <H3 style={{ marginBottom: 18 }}>
                                {party.tabName} Communication {required && <LabelRequiredSymbol />}
                            </H3>
                            <FormCardContainer>
                                <DeclarationInput
                                    required={required}
                                    condensed
                                    label="Identifier"
                                    maxLength={512}
                                    {...validatedFieldProps(addPathPrefix(party.path, 'communication.identifier'))}
                                />
                                <DeclarationSelect
                                    required={required}
                                    condensed
                                    hideCodelistMenu
                                    label="Type"
                                    codeValidation={[]}
                                    codelistProps={{
                                        codelistName: ICS2CodelistsMap.CL707,
                                        declarationType: DeclarationTypes.ICS2,
                                    }}
                                    {...validatedFieldProps(addPathPrefix(party.path, 'communication.type'))}
                                />
                            </FormCardContainer>
                        </div>
                    ) : (
                        <MultipleItemsCard
                            style={{ marginTop: 20 }}
                            condensed
                            section
                            required={!!party.communicationRequired && required}
                            viewOnly={viewOnly}
                            title={`${party.tabName} Communication`}
                            path={addPathPrefix(party.path, 'communication')}
                            initialValues={{ identifier: '', type: undefined }}
                            list={(list) => [
                                { field: 'Identifier', value: list.identifier },
                                { field: 'Type', value: list.type },
                            ]}
                        >
                            {(itemPath) => (
                                <FormCardContainer>
                                    <DeclarationInput
                                        required
                                        condensed
                                        label="Identifier"
                                        maxLength={512}
                                        {...validatedFieldProps(addPathPrefix(itemPath, 'identifier'))}
                                    />
                                    <DeclarationSelect
                                        required
                                        condensed
                                        hideCodelistMenu
                                        label="Type"
                                        codeValidation={[]}
                                        codelistProps={{
                                            codelistName: ICS2CodelistsMap.CL707,
                                            declarationType: DeclarationTypes.ICS2,
                                        }}
                                        {...validatedFieldProps(addPathPrefix(itemPath, 'type'))}
                                    />
                                </FormCardContainer>
                            )}
                        </MultipleItemsCard>
                    )}
                </>
            ),
        };
    });

    return (
        <NewFormCard hidden={hidden} title="Parties">
            <Spin spinning={filling} tip="Filling in party details...">
                <Tabs onChange={(key) => setActiveTabKey(key)} activeKey={activeTabKey} items={tabItems} />
            </Spin>
        </NewFormCard>
    );
};

export default PartiesCard;
